import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useNavigate } from "react-router-dom";
import * as api from "../services/api";

interface HeroSlide {
  id: number;
  title: string;
  description: string;
  imageUrl: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const HeroSlideList: React.FC = () => {
  const navigate = useNavigate();
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSlide, setSelectedSlide] = useState<HeroSlide | null>(null);

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      setLoading(true);
      const response = await api.get("/hero-slides");
      setSlides(response.data);
    } catch (error) {
      console.error("Error fetching hero slides:", error);
      alert("Gagal memuat data hero slides");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSlide) return;

    try {
      await api.deleteRequest(`/hero-slides/${selectedSlide.id}`);
      setDeleteDialogOpen(false);
      setSelectedSlide(null);
      fetchSlides();
    } catch (error) {
      console.error("Error deleting hero slide:", error);
      alert("Gagal menghapus hero slide");
    }
  };

  const handleToggleActive = async (slide: HeroSlide) => {
    try {
      const formData = new FormData();
      formData.append("title", slide.title);
      formData.append("description", slide.description);
      formData.append("order", slide.order.toString());
      formData.append("isActive", (!slide.isActive).toString());

      await api.put(`/hero-slides/${slide.id}`, formData);
      fetchSlides();
    } catch (error) {
      console.error("Error updating hero slide:", error);
      alert("Gagal mengubah status hero slide");
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;

    const newSlides = [...slides];
    const temp = newSlides[index - 1].order;
    newSlides[index - 1].order = newSlides[index].order;
    newSlides[index].order = temp;

    try {
      await api.patch("/hero-slides/reorder", {
        slides: [
          { id: newSlides[index - 1].id, order: newSlides[index - 1].order },
          { id: newSlides[index].id, order: newSlides[index].order },
        ],
      });
      fetchSlides();
    } catch (error) {
      console.error("Error reordering slides:", error);
      alert("Gagal mengubah urutan slide");
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === slides.length - 1) return;

    const newSlides = [...slides];
    const temp = newSlides[index + 1].order;
    newSlides[index + 1].order = newSlides[index].order;
    newSlides[index].order = temp;

    try {
      await api.patch("/hero-slides/reorder", {
        slides: [
          { id: newSlides[index].id, order: newSlides[index].order },
          { id: newSlides[index + 1].id, order: newSlides[index + 1].order },
        ],
      });
      fetchSlides();
    } catch (error) {
      console.error("Error reordering slides:", error);
      alert("Gagal mengubah urutan slide");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" component="h1">
            Kelola Hero Slides
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Kelola tampilan hero section di halaman utama website
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/hero-slides/new")}
        >
          Tambah Slide
        </Button>
      </Box>

      {slides.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            Belum ada hero slide. Klik tombol "Tambah Slide" untuk membuat yang
            baru.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Gambar</TableCell>
                <TableCell>Judul</TableCell>
                <TableCell>Deskripsi</TableCell>
                <TableCell>Urutan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {slides.map((slide, index) => (
                <TableRow key={slide.id}>
                  <TableCell>
                    <img
                      src={api.getImageUrl(slide.imageUrl)}
                      alt={slide.title}
                      style={{
                        width: 100,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                      {slide.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {slide.title.length}/40 karakter
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{slide.description}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {slide.description.length}/40 karakter
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip label={slide.order} size="small" />
                      <Box display="flex" flexDirection="column">
                        <IconButton
                          size="small"
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDown(index)}
                          disabled={index === slides.length - 1}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={slide.isActive}
                      onChange={() => handleToggleActive(slide)}
                      color="primary"
                    />
                    <Typography variant="caption" display="block">
                      {slide.isActive ? "Aktif" : "Nonaktif"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => navigate(`/hero-slides/edit/${slide.id}`)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setSelectedSlide(slide);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Hapus Hero Slide</DialogTitle>
        <DialogContent>
          <Typography>
            Apakah Anda yakin ingin menghapus slide "{selectedSlide?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Batal</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Hapus
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HeroSlideList;
